import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * WAR ROOM (Военный совет) — the COUNCIL chamber of Castle Hearthhold (ADR-0051): where the guild's
 * captains gather to plan the next raid. The composition is deliberately FOCUSED, not an arsenal: the
 * unmistakable HERO is the grand round COUNCIL TABLE dead-centre — a painted realm-map surface lit from
 * above by a warm CANDELABRA pool — ringed by eight matching HIGH-BACK chairs, every one facing the map.
 * The north wall tells the story (heraldic war BANNER above the engine campaign-map + a campaign
 * TAPESTRY), a perched RAVEN waits with messages by the east, and two trophy SHIELDS hang as campaign
 * honours flanking the table. Two wall torches add a warm edge so the strategy stays lamplit, never grim.
 *
 * Composition note (declutter pass): trimmed from a busy ring of 4 shields + a weapon-rack + 4 torches to
 * a symmetric frame — 2 trophy shields, 2 torches — so the council reads at a glance and the candelabra
 * stays the room's brightest beat. EMPTY FLOOR around the chair ring is intentional breathing room.
 *
 * Geometry: zone (2420,360) 380×400 → centre (2610,560). Doors at N(x2610), S(x2610), W(y560), E(y560)
 * are kept clear — set-dressing rings the interior + hugs the walls, never the central cross or a mouth.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;
  const cx = 2610;
  const cy = 560;

  // ── Floor: a deep council runner under the table ring (laid first, under everything). ──
  place(ctx, kit.runnerRug(art, 250, 250), cx, cy);

  // ── HERO: the grand round COUNCIL TABLE (a painted realm-map surface), dead-centre. ──
  place(ctx, kit.roundTable(art, 70), cx, cy);

  // ── The eight HIGH-BACK council chairs ringing the table, each facing the map centre.
  //    SIT-MATCH: a woodChair sits AT every council seat anchor (MEETING_SEATS) 1:1. ──
  for (const [sx, sy] of [
    [2610, 430],
    [2695, 468],
    [2730, 560],
    [2695, 652],
    [2610, 690],
    [2525, 652],
    [2490, 560],
    [2525, 468],
  ] as const) {
    const facing = Math.atan2(cy - sy, cx - sx); // tilt the high back toward the table centre
    place(ctx, kit.woodChair(art, facing), sx, sy);
  }

  // ── POOL LIGHT: a candelabra hung over the table — the room's brightest beat, pooling onto the map. ──
  place(ctx, kit.candelabra(art), cx, 500);

  // ── North wall: the heraldic war BANNER high + a campaign TAPESTRY map beside the engine wall-map. ──
  place(ctx, kit.banner(art, 0x8a2f2f, 110), cx, 398);
  place(ctx, kit.tapestry(art, 90, 120, 'map'), 2710, 408);

  // ── The messenger RAVEN on its perch by the east wall (dispatches + omens). ──
  place(ctx, kit.raven(art), 2740, 460);

  // ── Two trophy SHIELDS as campaign honours, hung symmetrically to flank the table on the side walls. ──
  place(ctx, kit.trophyShield(art, 0x2f4a8a), 2470, 500);
  place(ctx, kit.trophyShield(art), 2750, 660);

  // ── Two wall torches on the long walls (off the door mouths) — warm strategic edge light. ──
  place(ctx, kit.wallTorch(art), 2470, 640);
  place(ctx, kit.wallTorch(art), 2750, 540);
};

export default decorate;
